import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Layers, Package, FolderTree, Grid3X3 } from 'lucide-react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import api from '../../services/api';

const levelIcons = {
  department: { icon: '🏢', color: '#009995', bg: '#E6FAFB' },
  buying_group: { icon: '🛒', color: '#2563EB', bg: '#EFF6FF' },
  category: { icon: '📦', color: '#D97706', bg: '#FFFBEB' },
  subcategory: { icon: '📂', color: '#7C3AED', bg: '#F5F3FF' },
  product_type: { icon: '🏷️', color: '#059669', bg: '#ECFDF5' },
};

const levelLabels = {
  department: 'Отдел',
  buying_group: 'Группа закупа',
  category: 'Категория',
  subcategory: 'Подкатегория',
  product_type: 'Вид товара',
};

function CategoryNode({ node, depth = 0 }) {
  const [open, setOpen] = useState(depth < 1);
  const hasChildren = node.children && node.children.length > 0;
  const level = levelIcons[node.level] || levelIcons.category;

  return (
    <div>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: `10px ${16 + depth * 24}px`,
          borderBottom: '1px solid #F1F5F9',
          cursor: hasChildren ? 'pointer' : 'default',
          transition: 'background 0.12s',
        }}
        onClick={() => hasChildren && setOpen(o => !o)}
        onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFB'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Expand/collapse arrow */}
        <span style={{ display: 'flex', alignItems: 'center', color: '#94A3B8', width: 16, flexShrink: 0 }}>
          {hasChildren ? (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : (
            <span style={{ width: 14, height: 14, display: 'block' }} />
          )}
        </span>

        {/* Level icon */}
        <span style={{
          width: 28, height: 28, borderRadius: 8,
          background: level.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, flexShrink: 0,
        }}>
          {level.icon}
        </span>

        {/* Name */}
        <span style={{
          fontWeight: depth === 0 ? 700 : depth === 1 ? 600 : 500,
          fontSize: depth === 0 ? 14 : 13,
          color: '#0F172A', flex: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {node.name}
        </span>

        {/* Level label */}
        <Badge variant="gray" style={{ fontSize: 9, padding: '2px 6px', opacity: 0.7 }}>
          {levelLabels[node.level] || node.level}
        </Badge>

        {/* Product count */}
        <Badge variant={node.product_count > 0 ? 'primary' : 'gray'} style={{ fontSize: 10, minWidth: 48, textAlign: 'center' }}>
          {node.product_count} шт
        </Badge>

        {/* Children count */}
        {hasChildren && (
          <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500, flexShrink: 0 }}>
            ({node.children.length})
          </span>
        )}
      </div>

      {/* Children — animated expand */}
      <div style={{
        overflow: 'hidden',
        maxHeight: open && hasChildren ? 100000 : 0,
        transition: hasChildren ? 'max-height 0.3s ease' : 'none',
      }}>
        {hasChildren && node.children.map((ch, i) => (
          <CategoryNode key={`${ch.level}-${ch.name}-${i}`} node={ch} depth={depth + 1} />
        ))}
      </div>
    </div>
  );
}

export default function ProductCategories() {
  const [tree, setTree] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalCategories, setTotalCategories] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await api.get('/v1/products/categories-tree');
        if (res?.data) {
          setTree(res.data.tree || []);
          setTotalProducts(res.data.total_products || 0);
          setTotalCategories(res.data.total_categories || 0);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  // Top-level summary cards
  const summaryCards = [
    { icon: <Layers size={20} />, label: 'Всего узлов', value: totalCategories, color: '#009995', bg: '#E6FAFB' },
    { icon: <Package size={20} />, label: 'Товаров', value: totalProducts, color: '#2563EB', bg: '#EFF6FF' },
    { icon: <FolderTree size={20} />, label: 'Отделов', value: tree.length, color: '#D97706', bg: '#FFFBEB' },
  ];

  // Quick grid of top-level departments
  const levelLegend = [
    { emoji: '🏢', label: 'Отдел', level: 'department' },
    { emoji: '🛒', label: 'Группа закупа', level: 'buying_group' },
    { emoji: '📦', label: 'Категория', level: 'category' },
    { emoji: '📂', label: 'Подкатегория', level: 'subcategory' },
    { emoji: '🏷️', label: 'Вид товара', level: 'product_type' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, animation: 'fadeIn 0.3s ease' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>
          Категории товаров
        </h1>
        <p style={{ color: '#94A3B8', fontSize: 13, marginTop: 2, fontWeight: 500 }}>
          Иерархия из 1С: Отдел &rarr; Группа закупа &rarr; Категория &rarr; Подкатегория &rarr; Вид товара
        </p>
      </div>

      {/* Summary cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 12,
      }}>
        {summaryCards.map(card => (
          <Card key={card.label} padding="16px">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12,
                background: card.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: card.color, flexShrink: 0,
              }}>
                {card.icon}
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>
                  {loading ? '...' : card.value.toLocaleString('ru-RU')}
                </div>
                <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500 }}>{card.label}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Legend */}
      <Card padding="12px 16px">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>Уровни:</span>
          {levelLegend.map(l => (
            <span key={l.level} style={{
              display: 'flex', alignItems: 'center', gap: 4,
              fontSize: 12, color: '#475569', fontWeight: 500,
            }}>
              <span style={{ fontSize: 14 }}>{l.emoji}</span>
              {l.label}
            </span>
          ))}
        </div>
      </Card>

      {/* Department grid — top-level only */}
      {!loading && tree.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 12,
        }}>
          {tree.map((dept, i) => (
            <div key={`dept-${i}`} style={{
              background: '#fff', borderRadius: 14, padding: '18px 14px', textAlign: 'center',
              border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              transition: 'all 0.2s ease', cursor: 'default',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>🏢</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#0F172A', marginBottom: 4 }}>
                {dept.name}
              </div>
              <div style={{ fontSize: 12, color: '#009995', fontWeight: 600 }}>
                {dept.product_count.toLocaleString('ru-RU')} товаров
              </div>
              {dept.children && dept.children.length > 0 && (
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
                  {dept.children.length} групп
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Full tree */}
      <Card title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Grid3X3 size={16} color="#009995" />
          Дерево классификации
        </span>
      } padding="0">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#94A3B8' }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              border: '3px solid rgba(0,206,238,0.15)', borderTopColor: '#00CEEE',
              animation: 'spin 0.8s linear infinite', margin: '0 auto 10px',
            }} />
            Загрузка классификации...
          </div>
        ) : tree.length > 0 ? (
          tree.map((node, i) => <CategoryNode key={`root-${i}`} node={node} depth={0} />)
        ) : (
          <div style={{ textAlign: 'center', padding: 48, color: '#94A3B8', fontSize: 13 }}>
            <div style={{ fontSize: 40, marginBottom: 8, opacity: 0.5 }}>📦</div>
            Категории пока не загружены.<br />
            Данные формируются из товаров, синхронизированных из 1С.
          </div>
        )}
      </Card>
    </div>
  );
}
